import { Brand } from 'effect'
import { z } from 'zod'

export type RaceEventRowDTO = {
  event_timestamp: string
  event_type: string
  id: string
  payload:
    | {
        results: {
          marbleId: string
          position: number
          marbleColor: string
          marbleNumber: number
        }[]
      }
    | { start_timestamp: string }
    | { crash_multiplier: number }
    | Record<string, unknown>
    | null
  race_configuration_id: string
  race_id: string
} & Brand.Brand<'RaceEventRowDTO'>

export const RaceEventRowDTORawSchema = z.object({
  event_timestamp: z.string(),
  event_type: z.string(),
  id: z.string(),
  payload: z
    .union([
      // race_settled payload — `streamMetadata` is CCTV-only and
      // optional; classic games omit it entirely. `marketResults`
      // is chip-placement-only (configured side-bet markets) and
      // also optional; markets with no winner this round are
      // omitted from the map entirely.
      z.object({
        results: z.array(
          z.object({
            marbleId: z.string(),
            position: z.number(),
            marbleColor: z.string(),
            marbleNumber: z.number()
          })
        ),
        streamMetadata: z
          .object({
            location: z.string().optional(),
            streamKey: z.string().optional(),
            detectionType: z.string().optional()
          })
          .optional(),
        finalCount: z.number().optional(),
        marketResults: z
          .record(
            z.array(
              z.object({
                marbleId: z.string(),
                position: z.number(),
                marbleColor: z.string(),
                marbleNumber: z.number()
              })
            )
          )
          .optional()
      }),
      // race_scheduled payload
      z.object({
        start_timestamp: z.string()
      }),
      // race_ended payload (crash games)
      z.object({
        crash_multiplier: z.number()
      }),
      // race_started payload (CCTV-only — boundaries + per-option
      // labels + optional location for the toolbar). Crash games
      // also attach a payload to race_started (`{game_mode: "crash"}`)
      // but that shape doesn't have these fields and falls through
      // to the passthrough variant below, preserving its original
      // keys via `.passthrough()`.
      z.object({
        boundaries: z.object({
          lower: z.number(),
          upper: z.number()
        }),
        optionLabels: z.record(z.string()),
        location: z.string().optional()
      }),
      // Other event types may have different payloads or empty objects
      z.object({}).passthrough()
    ])
    .nullable(),
  race_configuration_id: z.string(),
  race_id: z.string()
})

const brandToRaceEventRowDTO = Brand.nominal<RaceEventRowDTO>()

export const RaceEventRowDTOSchema = RaceEventRowDTORawSchema.transform(brandToRaceEventRowDTO)
